import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'

import { getFileUrl } from '@utils/getFileUrl'

import { useModal } from '@hooks/app/modal.hook'

import { Modal } from '@components/Modal'
import { Input } from '@components/Input'
import { TextArea } from '@components/TextArea'
import { Refresh } from '@components/Refresh'
import { UploadFile } from '@components/UploadFile'

import { useProjectFile } from '@hooks/project/project-file.hook'
import { useUpdateProjectFile } from '@hooks/project/update-project-file.hook'

import { LocalFile, RemoteFile } from '@types'

import { removeExtension } from '@utils/string'

import { Container } from './edit.styled'

export const EditPlanPage = (): JSX.Element => {
  const { hideModal } = useModal()

  const { t } = useTranslation()

  const [searchParams] = useSearchParams()

  const projectFileId = searchParams.get('planId')
  const { projectId } = useParams()

  const { data, loading, error, refetch } = useProjectFile(projectFileId || '')

  const isLoading = loading ? 1 : error ? -1 : 0

  const projectFile = data?.projectFile

  const [name, setName] = useState<string>('')
  const [description, setDescription] = useState<string>('')

  const [files, setFiles] = useState<(LocalFile | RemoteFile)[]>([])

  const { updateProjectFile } = useUpdateProjectFile()

  const loadPlanPreview = async () => {
    if (projectFile) {
      const formattedFiles: RemoteFile[] = []
      const url = await getFileUrl(projectFile.url)

      const remoteFile: RemoteFile = {
        deleted: false,
        id: projectFile.file.id,
        name: projectFile.file.name,
        type: projectFile.file.type,
        extension: projectFile.file.extension,
        url,
      }

      formattedFiles.push(remoteFile)

      setFiles(formattedFiles)
    }
  }

  useEffect(() => {
    if (projectFile) {
      setName(projectFile.name)
      setDescription(projectFile.description || '')

      loadPlanPreview()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectFile])

  const handleFileChange = (files: (LocalFile | RemoteFile)[]) => {
    setFiles(() => [...files] as LocalFile[])

    if (files && files.length > 0) {
      const fileName = removeExtension(files[0].name)
      setName(fileName)
    } else {
      setName('')
    }
  }

  return (
    <Modal
      title={t('modules_project-plans_pages_edit_plan_title')}
      onSubmitLabel={t('modules_project-plans_pages_edit_plan_button_edit-plan')}
      onClose={() => {
        hideModal('update_plan', { planId: projectFileId })
      }}
      onSubmit={() => {
        if (projectFile && projectId) {
          let localFile: LocalFile | undefined
          const file = files[0]
          if (Object.hasOwn(file, 'data')) {
            localFile = file as LocalFile
          }

          updateProjectFile(
            {
              projectFileId: projectFile.id,
              name,
              description,
              folderId: projectFile.folderId,
            },
            localFile,
          )
        }

        hideModal('update_plan', { planId: projectFileId })
      }}
    >
      <Container>
        <Refresh small loading={isLoading} onRefresh={() => refetch(projectFile?.id || '')}>
          {projectFile && (
            <>
              <Input
                autoFocus
                required
                label={t('modules_project-plans_pages_edit_plan_label_plan-name')}
                placeholder={t('modules_project-plans_pages_edit_plan_placeholder_plan-name')}
                value={name}
                onChange={(v) => setName(v)}
              />
              <TextArea
                label={t('modules_project-plans_pages_edit_plan_label_description')}
                placeholder={t('modules_project-plans_pages_edit_plan_placeholder_description')}
                value={description}
                onChange={(v) => setDescription(v)}
              />

              <UploadFile
                required
                label={t('modules_project-plans_pages_edit_project_label_project_plan')}
                labelUpload={t('modules_project-plans_pages_edit_project_label_upload_plan')}
                files={files}
                setFiles={handleFileChange}
              />
            </>
          )}
        </Refresh>
      </Container>
    </Modal>
  )
}
